import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {catchError, EMPTY, forkJoin, of, take} from 'rxjs';
import {map} from 'rxjs/operators';
import {ReferenceService} from '../../../../onboarding-admin-portal/services/reference.service';
import {ReferenceDTO} from '../../../../onboarding-admin-portal/models/application/ReferenceDTO';
import {ReferenceLookupValue} from 'src/app/modules/clm/types/reference-lookup-value.enum';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {MemberVerificationService} from '../../../../clm/services/member-verification.service';
import {VerificationStatus} from '../../../../common/types/common.types';
import {
  BankVerificationRequest,
  BankVerificationResponse
} from '../../../../common/models/ExternalMemberVerificationDTO';
import {DisplayStrings} from '../../../../common/constants/DisplayStrings';
import {CustomValidator} from '../../../../common';
import {getStatusColor} from '../../../../common/util/Util';

enum Controls {
  IdNumber = 'idNumber',
  IdType = 'idType',
  BankCode = 'bankName',
  BranchCode = 'branchCode',
  AccountType = 'accountType',
  AccountNumber = 'accountNumber',
  Initials = 'initials',
  Surname = 'surname'
}


@Component({
  selector: 'app-adhoc-bank-verification-dialog',
  templateUrl: './adhoc-bank-verification-dialog.component.html',
  styleUrls: ['./adhoc-bank-verification-dialog.component.scss']
})
export class AdhocBankVerificationDialogComponent implements OnInit {
  readonly getStatusColor = getStatusColor;

  readonly controls = Controls;
  readonly idNumberErrorMessage = DisplayStrings.idErrorMessagesList;
  readonly CustomValidator = CustomValidator;

  verificationForm: FormGroup;
  verificationResponse: any;
  verificationDisplayProperties: any[] = [];

  idTypeOptions: NameValueDTO[] = [];
  bankCodeOptions: NameValueDTO[] = [];


  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<AdhocBankVerificationDialogComponent>,
    private verificationService: MemberVerificationService,
    private referenceService: ReferenceService,
  ) {
  }

  ngOnInit(): void {
    this.initForm();
    this.getListOfLookupValues();
  }

  onVerify(): void {
    console.log('Form Valid on Verify:', this.verificationForm.valid);
    console.log('Form Values on Verify:', this.verificationForm.getRawValue());

    if (this.verificationForm.valid) {
      const formValues = this.verificationForm.getRawValue();

      const request: BankVerificationRequest = {
        [Controls.IdNumber]: formValues[Controls.IdNumber],
        [Controls.IdType]: formValues[Controls.IdType],
        [Controls.BankCode]: formValues[Controls.BankCode],
        [Controls.BranchCode]: formValues[Controls.BranchCode],
        [Controls.AccountType]: formValues[Controls.AccountType],
        [Controls.AccountNumber]: formValues[Controls.AccountNumber],
        [Controls.Initials]: formValues[Controls.Initials],
        [Controls.Surname]: formValues[Controls.Surname]
      };

      this.verificationService.bankVerification(request)
        .pipe(
          take(1),
          catchError(error => {
            console.error('Bank verification error:', error);
            return of(null);
          })
        )
        .subscribe(response => {
          if (response) {
            this.verificationResponse = response;
            this.setVerificationDisplayProperties(response);
          }
        });
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  initForm(): void {
    this.verificationForm = this.formBuilder.group({
      [Controls.IdNumber]: [null, [Validators.required]],
      [Controls.IdType]: [null, [Validators.required]],
      [Controls.BankCode]: [null, [Validators.required]],
      [Controls.BranchCode]: [null, [Validators.required]],
      [Controls.AccountType]: [{value: 'CURRENTCHEQUEACCOUNT', disabled: true}],
      [Controls.AccountNumber]: [null, [Validators.required]],
      [Controls.Initials]: [null, [Validators.required]],
      [Controls.Surname]: [null, [Validators.required]]
    });
  }

  onSelectionIDTypeChange(idType: NameValueDTO) {
    this.verificationForm.get(Controls.IdType).setValue(idType.value);
  }

  onSelectionBankChange(bank: NameValueDTO) {
    this.verificationForm.get(Controls.BankCode).setValue(bank.value);
  }

  private getListOfLookupValues(): void {
    forkJoin({
      bankCodes: this.referenceService.getAllByFieldName(ReferenceLookupValue.BankCode),
      idTypes: this.referenceService.getAllByFieldName(ReferenceLookupValue.IdTypeCode)
    }).pipe(
      take(1),
      map(response => ({
        bankCodes: this.mapReferenceToNameValue(response.bankCodes),
        idTypes: this.mapReferenceToNameValue(response.idTypes)
      })),
      catchError(error => {
        console.error('Error fetching lookup values:', error);
        return EMPTY;
      })
    ).subscribe(result => {
      this.bankCodeOptions = result.bankCodes;
      this.idTypeOptions = result.idTypes;
    });
  }

  private mapReferenceToNameValue(references: ReferenceDTO[]): any {
    if (!references || references.length === 0) {
      return [];
    }

    return references
      .sort((a, b) => a.value.localeCompare(b.value))
      .map((ref: ReferenceDTO) => ({
        name: ref.value,
        value: ref.key
      }));
  }

  private setVerificationDisplayProperties(response: BankVerificationResponse): void {
    this.verificationDisplayProperties = [
      { name: 'Account Found', value: this.getValueFromResponse(response.accountFound), showIndicator: this.isValidResponse(response.accountFound), status: response.accountFound ? VerificationStatus.Success : VerificationStatus.Error },
      { name: 'ID Number Match', value: this.getValueFromResponse(response.idNumberMatch), showIndicator: this.isValidResponse(response.idNumberMatch), status: response.idNumberMatch ? VerificationStatus.Success : VerificationStatus.Error },
      { name: 'Initials Match', value: this.getValueFromResponse(response.initialsMatch), showIndicator: this.isValidResponse(response.initialsMatch), status: response.initialsMatch ? VerificationStatus.Success : VerificationStatus.Error },
      { name: 'Surname Match', value: this.getValueFromResponse(response.surnameMatch), showIndicator: this.isValidResponse(response.surnameMatch), status: response.surnameMatch ? VerificationStatus.Success : VerificationStatus.Error },
      { name: 'Account Open', value: this.getValueFromResponse(response.accountOpen), showIndicator: this.isValidResponse(response.accountOpen), status: response.accountOpen ? VerificationStatus.Success : VerificationStatus.Error }
    ];
  }

  private getValueFromResponse(response: boolean) {
    return this.isValidResponse(response) ? response : 'N/A';
  }

  private isValidResponse(response: boolean) {
    return response != null;
  }
}
